import {defineMessages, useIntl} from 'react-intl';
import {map, merge, Subscription, takeWhile} from 'rxjs';
import {useEffect, useState} from 'react';

import type {Attachment} from '@stripe-internal/conversations-client';
import {
  attachmentsEnabled,
  inputEnabled,
} from '@stripe-internal/conversations-assistant';
import {
  ClientStatus,
  Conversation,
  ConversationLogItem,
} from '@stripe-internal/conversations-client';
import {
  ConnectionNotice,
  useAnalytics,
  useFileStaging,
} from '@stripe-internal/conversations-ui';
import type {StagedFile} from '@stripe-internal/conversations-ui';

import {MobileInput} from './MobileInput';

const INTERNAL_TEST_STRING = /^\s*stripe\s+internal\s+test\s*$/i;
const INTERNAL_SYNTHETIC_TEST_STRING =
  /^\s*stripe\s+internal\s+synthetic\s+test\s*$/i;

const messages = defineMessages({
  emptyPlaceholder: {
    id: 'mobile_conversations_assistant.conversation.chatInput.emptyPlaceholder',
    description:
      'Label for the chat textbox when no messages have been sent yet',
    defaultMessage: 'How can we help?',
  },
  replyPlaceholder: {
    id: 'mobile_conversations_assistant.conversation.chatInput.replyPlaceholder',
    description: 'Label for the chat textbox when there is an ongoing chat',
    defaultMessage: 'Write a reply…',
  },
  promptPlaceholder: {
    id: 'mobile_conversations_assistant.conversation.chatInput.promptPlaceholder',
    description:
      'Label for the chat textbox when the user has the option to type out their question against a chatbot',
    defaultMessage: 'Ask a question',
  },
});

const logContainsUserMessage = (items: ConversationLogItem[]): boolean => {
  return items.some(
    (item: ConversationLogItem) =>
      item.name === 'conversation-message-added' &&
      item.message.participant.role === 'user',
  );
};

const logContainsAgentMessages = (items: ConversationLogItem[]): boolean => {
  return items.some(
    (item: ConversationLogItem) =>
      item.name === 'conversation-message-added' &&
      item.message.participant.role === 'agent',
  );
};

const usePlaceholderValue = (conversation: Conversation): string => {
  const [agentHasSentMessage, setAgentHasSentMessage] = useState<boolean>(
    logContainsAgentMessages(conversation.log.get()),
  );

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(
        takeWhile(() => !agentHasSentMessage),
        map(logContainsAgentMessages),
      )
      .subscribe(setAgentHasSentMessage);
    return () => sub.unsubscribe();
  }, [conversation, agentHasSentMessage]);

  const intl = useIntl();

  if (conversation.context.channel === 'bot') {
    return intl.formatMessage(messages.promptPlaceholder);
  } else {
    return agentHasSentMessage
      ? intl.formatMessage(messages.replyPlaceholder)
      : intl.formatMessage(messages.emptyPlaceholder);
  }
};

export const MobileAssistantInput = ({
  conversation,
}: {
  conversation: Conversation;
}) => {
  const analytics = useAnalytics();
  const [clientStatus, setClientStatus] = useState<ClientStatus>(
    conversation.client.getStatus(),
  );

  const [userHasSentMessage, setUserHasSentMessage] = useState<boolean>(
    logContainsUserMessage(conversation.log.get()),
  );

  const [showInput, setShowInput] = useState<boolean>(
    inputEnabled(conversation),
  );
  const [disableAttachments, setDisableAttachments] = useState<boolean>(
    !attachmentsEnabled(conversation),
  );

  const placeholder = usePlaceholderValue(conversation);
  const {stagedFiles, stageFile, unstageFile, clearStaged, uploadErrors} =
    useFileStaging('universal_help_attachment');
  const isBotContext = conversation.context.channel === 'bot';

  useEffect(() => {
    const sub = new Subscription();

    sub.add(conversation.client.statusObservable().subscribe(setClientStatus));

    sub.add(
      merge(
        conversation.statusObservable(),
        conversation.contextObservable(),
      ).subscribe(() => {
        setShowInput(inputEnabled(conversation));
        setDisableAttachments(!attachmentsEnabled(conversation));
      }),
    );

    return () => sub.unsubscribe();
  }, [conversation]);

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(
        takeWhile(() => !userHasSentMessage),
        map(logContainsUserMessage),
      )
      .subscribe(setUserHasSentMessage);
    return () => sub.unsubscribe();
  }, [conversation, userHasSentMessage]);

  if (
    clientStatus === ClientStatus.Offline ||
    clientStatus === ClientStatus.Reconnecting
  ) {
    return <ConnectionNotice status="reconnecting" />;
  }

  const inputDisabled = clientStatus !== ClientStatus.Connected;

  const handleSendMessage = async (message: string) => {
    if (isBotContext && stagedFiles.length > 0) {
      const attachments: Attachment[] = stagedFiles
        .filter((sf: StagedFile) => sf.status === 'uploaded')
        .map((sf) => ({
          name: sf.file.name,
          size: sf.file.size,
          type: sf.file.type,
          upload_token: sf.uploadToken,
        }));
      const msg = conversation.buildTextWithAttachmentsMessage(
        message,
        attachments,
      );
      await conversation.sendMessage(msg);
      if (attachments.length > 0) {
        analytics.action('universal_help_attachment_message_sent', {
          attachmentCount: attachments.length,
          hasText: message.trim() !== '',
        });
      }
      clearStaged();
    } else if (message.trim() === '') {
      return;
    }

    if (message.match(INTERNAL_TEST_STRING)) {
      conversation.setContextValue('dev', true);
      return;
    } else if (message.match(INTERNAL_SYNTHETIC_TEST_STRING)) {
      conversation.setContextValue('synthetic', true);
      return;
    }

    const msg = conversation.buildTextMessage(message);
    await conversation.sendMessage(msg);
  };

  const handleSendFile = async (file: File) => {
    if (isBotContext) {
      stageFile(file);
    } else {
      const msg = conversation.buildAttachmentMessage(file);
      await conversation.sendMessage(msg);
    }
  };

  const handleSendTyping = () => {
    conversation.sendTyping();
  };

  return (
    <MobileInput
      disabled={inputDisabled}
      placeholder={placeholder}
      onSendMessage={handleSendMessage}
      onSendFile={handleSendFile}
      onSendTyping={handleSendTyping}
      attachmentDisabled={disableAttachments || !userHasSentMessage}
      stagedFiles={stagedFiles}
      onRemoveStagedFile={unstageFile}
      uploadErrors={uploadErrors}
      hidden={!showInput || !!conversation.context?.placeholderConversation}
    />
  );
};
